import captureError from '@disco/utils/captureError'
import * as React from 'react'
import { checkIfConnectError } from '@disco/utils/captureError'
import { Spinner } from 'components/ui'
import Button from 'components/ui/button'
import DiscoModal from 'components/ui/disco-modal'
import styles from './index.module.css'

type Props = React.ComponentProps<typeof DiscoModal> & {
  fallbackTitle?: string
  loadData: () => Promise<any>
  render: () => React.ReactNode
  /** Optional loading UI, e.g. a skeleton, instead of the default spinner. */
  renderLoading?: () => React.ReactNode
  isLoading?: boolean
  supportsRetry: boolean
  error?: Error | null | undefined
} // this can catch only errors inside modal, not around it(ie connect(...))
// TODO: deprecate, don't use

/**
 * A modal that loads data asynchronously with loading, error, and retry states
 * @param fallbackTitle - Optional title to display during loading and error states
 * @param loadData - Function that returns a promise to load the modal data
 * @param render - Function that renders the modal content when data is loaded
 * @param isLoading - External loading state to override internal loading
 * @param supportsRetry - Whether to show retry button on error - defaults to true
 * @param error - External error to display instead of caught errors
 * @param props - Additional props passed to the underlying DiscoModal component
 * @returns A DiscoModal with async data loading, error handling, and retry functionality
 */
export default class DiscoAsyncModal extends React.Component<
  Props,
  {
    data: Record<string, any> | null | undefined
    error: Error | null | undefined
    loaded: boolean
  }
> {
  static defaultProps: {
    supportsRetry: boolean
  } = {
    supportsRetry: true,
  }
  state: {
    data: any | null | undefined
    error: Error | null | undefined
    loaded: boolean
  } = {
    data: null,
    error: null,
    loaded: false,
  }

  componentDidMount() {
    this._load()
  }

  async _load() {
    try {
      const data = await this.props.loadData()
      this.setState({
        data,
        loaded: true,
        error: null,
      })
    } catch (err) {
      const isConnectError = checkIfConnectError(err)
      captureError(err)
      this.setState({
        error: err,
        loaded: true,
      })

      if (isConnectError && this.props.onRequestClose) {
        this.props.onRequestClose()
      }
    }
  }

  _handleRetry: () => void = () => {
    this.setState({
      loaded: false,
    })
    setTimeout(() => {
      // let UI respond to user action, then start loading
      this._load()
    }, 120)
  }

  _renderState(children: React.ReactNode): React.ReactElement<'div'> {
    const { fallbackTitle } = this.props
    const title = fallbackTitle && (
      <div className={styles.discoAsyncModalTitle}>{fallbackTitle}</div>
    )
    return (
      <div className={styles.discoAsyncModalCont}>
        {title}
        {children}
      </div>
    )
  }

  _renderLoadingState(): React.ReactNode {
    if (this.props.renderLoading) return this.props.renderLoading()
    return this._renderState(
      <div>
        <Spinner />
      </div>,
    )
  }

  _renderErrorState(error: Error): React.ReactElement<'div'> {
    const { supportsRetry } = this.props
    return this._renderState(
      <div>
        <Spinner paused red />
        <div className={styles.discoAsyncModalError}>{error.toString()}</div>
        {supportsRetry && (
          <Button
            caption="Retry"
            progressIndicator={this.props.isLoading}
            progressCaption="Retrying"
            onClick={this._handleRetry}
          />
        )}
      </div>,
    )
  }

  render(): React.ReactNode {
    const {
      render,
      isLoading: propsIsLoading,
      renderLoading: _renderLoading,
      error: propsError,
      loadData,
      supportsRetry,
      fallbackTitle,
      ...restProps
    } = this.props
    let error = this.state.error || propsError
    let isLoading = true

    if (this.state.loaded) {
      try {
        isLoading = propsIsLoading
        restProps.children = render()
      } catch (err) {
        error = err
      }
    }

    if (isLoading) {
      restProps.children = this._renderLoadingState()
    } else if (error) {
      restProps.children = this._renderErrorState(error)
    }

    return (
      <DiscoModal
        {...restProps}
        renderError={(details: { isConnectError: boolean; error: Error }) => (
          <DiscoModal {...restProps}>
            {this._renderErrorState(details.error)}
          </DiscoModal>
        )}
        errorHandler={(details: { isConnectError: boolean; error: Error }) => {
          if (details.isConnectError && this.props.onRequestClose) {
            this.props.onRequestClose()
          } else {
            // display error and let user close
          }
        }}
      />
    )
  }
}
